<template>
  <esg-base-table
      :items="items"
      :fields="fields"
      :max-height="maxHeight"
      :clickable="clickable"
      :selectable="selectable"
      :selectedItems="selectedItems"
      :size="size"
      @sortSwitched="switchSort"
      @rowClicked="onRowClick"
      @selected="onSelect"
  >
    <template #colgroup>
      <slot name="colgroup"></slot>
    </template>
    <template v-for="field in fields"
              v-slot:[slotname(field)]="{ value, item, items, field, row, col }">
      <slot :name="slotname(field)"
            :value="value"
            :item="item"
            :items="items"
            :field="field"
            :row="row"
            :col="col"></slot>
    </template>
  </esg-base-table>
</template>

<script>
import EsgBaseTable from "@/components/esg/base/EsgBaseTable.vue";

export default {
  components: { EsgBaseTable },
  props: {
    clickable: {
      type: Boolean,
      default: true,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      required: false
    },
    maxHeight: {
      type: String,
      default: 'auto'
    },
    endpoint: {
      type: String,
      required: true,
    },
    metadata: {
      type: Array,
      required: true,
    },
    filters: {
      type: Array,
      required: true
    },
    pageInfo: {
      type: Object,
      required: true
    },
    size: {
      type: String,
      default: 'md'
    }
  },
  computed: {
    columns() {
      return this.fields.map(col => col.name);
    },
    sorts() {
      return this.fields.filter(field => field.sortable && field.sortOrder).map(field => {
        return {
          name: field.name,
          order: field.sortOrder
        }
      });
    }
  },
  data() {
    return {
      loading: false,
      items: [],
      fields: [],
      formatters: {
        Year: this.formatYear,
        Date: this.formatDate,
        DateTime: this.formatDatetime,
        Code: this.formatCode,
        String: this.formatString,
        Number: this.formatFloat,
        Integer: this.formatInteger,
        Reference: this.formatString,
      }
    }
  },
  watch: {
    metadata: {
      immediate: true,
      deep: true,
      async handler(meta) {
        if (meta.length > 0) {
          await this.initialize();
          await this.fetchData();
        }
      }
    },
    filters: {
      deep: true,
      handler() {
        this.fetchData();
      }
    },
    pageInfo: {
      deep: true,
      handler() {
        this.fetchData();
      }
    },
    fields: {
      deep: true,
      handler() {
        this.fetchData();
      }
    }
  },
  methods: {
    async initialize() {
      if (this.metadata.length === 0) return;

      try {
        this.setLoading(true);

        // Meta information for columns
        let fields = structuredClone(this.metadata);

        // Replace dot(.) in column names with hyphen(_) and use as a key for the column
        fields.forEach(field => field.key = field.name.replace('.', '_'));

        // Reorder fields
        fields.sort((a, b) => a.order - b.order);

        // Process sorting info
        this.processSorting(fields);

        // Process common codes info
        await Promise.all(this.processCommonCodes(fields));

        // Set formatter
        fields.forEach(field => field.formatter = this.formatters[field.type || 'String'](field));

        this.fields = fields;

        this.$emit('initialized', this.fields);
      } catch (error) {
        this.setLoading(false);
        await this.showError(error);
      } finally {
        this.setLoading(false);
      }
    },
    async fetchData() {
      if (!this.endpoint) return;

      try {
        this.setLoading(true);

        // Construct query parameters
        let params = {
          columns: this.columns,
          filters: this.filters,
          sorts: this.sorts,
          pageNum: this.pageInfo.curr,
          pageSize: this.pageInfo.perPage,
        };

        // Load data
        let dataResp = await axios.get(this.endpoint, {
          headers: { 'Authorization': accessToken() },
          params
        });

        this.items = dataResp.data.list || dataResp.data;

        // Create a field 'selected' if the table is selectable
        if (this.selectable) {
          this.items.forEach(item => this.$set(item, 'selected', false));
        }

        this.$emit('update:total', dataResp.data.total);
      } catch (error) {
        this.setLoading(false);
        await this.showError(error);
      } finally {
        this.setLoading(false);
      }
    },
    processSorting(fields) {
      fields.forEach(field => field.sortable && (field.sortOrder = null));
    },
    processCommonCodes(fields) {
      return fields.map(async field => {
        if (field.type === 'Code') {

          // Construct filters for tenant ID
          let filters = [{
            name: 'esg_base_common_code.tenant_id',
            operation: 'EQUAL',
            value: this.tenantId()
          }];

          // and categories
          field.categories.forEach((cat, index) => {
            filters.push({
              name: `esg_base_common_code.category_${index + 1}`,
              operation: 'EQUAL',
              value: cat
            })
          });

          // Fetch code info
          let response = await axios.get(`/esg/lca/tables/esg_base_common_code`, {
            headers: { 'Authorization': accessToken() },
            params: { filters }
          });

          // Create options
          let options = []
          let codes = response.data.list
          codes.sort((a, b) => a.sequence - b.sequence)
          if (codes) {
            codes.forEach(code => options.push({ value: code.id, text: code.label }));
          }
          field.options = options;
        }
      });
    },
    formatDate() {
      return (datetime) => !datetime ? null : this.$moment(datetime).format("YYYY-MM-DD");
    },
    formatDatetime() {
      return (datetime) => !datetime ? null : this.$moment(datetime).format("YYYY-MM-DD HH:mm:ss");
    },
    formatYear() {
      return (datetime) => !datetime ? null : this.$moment(datetime).format("YYYY");
    },
    formatCode(field) {
      return (value) => {
        const code = field.options.find(opt => opt.value === value);
        return code ? code.text : null;
      }
    },
    formatString() {
      return val => val;
    },
    formatInteger() {
      return val => this.formatNumber(val, 0)
    },
    formatFloat() {
      return val => {
        if (!Number(val)) return val;
        return this.formatNumber(val, 2, 6);
      }
    },
    switchSort(name) {
      let field = this.fields.find(field => field.name === name);
      if (!field.sortable) return;

      // Switch sorting order
      if (!field.sortOrder) {
        field.sortOrder = 'ASC';
      } else if (field.sortOrder === 'ASC') {
        field.sortOrder = 'DESC';
      } else if (field.sortOrder === 'DESC') {
        field.sortOrder = null;
      }
    },
    slotname(field) {
      return `cell(${field.name})`;
    },
    setLoading(bool) {
      this.$emit('update:loading', bool);
    },
    onRowClick(item, index) {
      this.$emit('rowClicked', item, index);
    },
    onSelect(item, items, index) {
      this.$emit('selected', item, items, index);
    }
  },
}
</script>